import React, { createContext, useContext, useEffect, useState } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { split, last, head } from 'lodash';

import { useConfig } from './config.ctx';
import { useLazyGetApiUserQuery, useLazyLoginApiUserQuery, useLazyLogoutApiUserQuery } from 'app/apis/user.api'

export const AuthContext = createContext({});

export const ROLES = { administrativeRole: "administrative-role" }
export const PRIVILEGES = {
  editInstruments: "editInstruments",
  editMetaLists: "editMetaLists",
  publishInstruments: "publishInstruments",
  viewMonitoring: "viewMonitoring",
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const { adfsScopes } = useConfig();
  const isAuthenticated = useIsAuthenticated();
  const { instance: msalInstance, inProgress, accounts } = useMsal();

  const [loginApiUser] = useLazyLoginApiUserQuery();
  const [logoutApiUser] = useLazyLogoutApiUserQuery();
  const [getApiUser] = useLazyGetApiUserQuery();

  const login = () => {
    let redirectUri = window.location.href;
    if(redirectUri.includes('#')) {
      const parts = split(redirectUri, '#');
      redirectUri = head(parts);
      const anchor = last(parts);
      sessionStorage.setItem('anchor', anchor);
    }
    return msalInstance.loginRedirect({
      scopes: adfsScopes,
      redirectUri,
    }).catch(e => console.error('Login failed:', e))
  };

  const logout = () => {
    logoutApiUser();
    return msalInstance.logoutRedirect();
  };

  useEffect(() => {
    if (inProgress !== 'none') return;

    getApiUser().unwrap()
      .then(async (usr) => {
        const isAnon = usr.name === 'anonymous';
        const isIntra = usr.zone === 'intranet';
        
        if (!isAuthenticated && isIntra) {
          return login();
        }

        if (isAuthenticated && isIntra && isAnon) {
          return loginApiUser().unwrap().then(apiUsr => setCurrentUser(apiUsr))
        }
        
        setCurrentUser(await addDevSystPrivilegesAndRole(usr));
      });
  }, [isAuthenticated, msalInstance, inProgress]);

  const addDevSystPrivilegesAndRole = async (user) => {
    return fetch(`/dev-sys-privileges.json`)
      .then(response => response.ok ? response.json() : {})
      .then(data => {
        if (user.name !== data.name)
          return user;

        const devSysWithPrivilege = {
          ...user,
          role: data.permissions.role ?? '',
          privileges: data.permissions.privileges ?? [],
        };
        
        console.log('login check', user, 'isAuthenticated', isAuthenticated);
        console.log('devSyst priv', devSysWithPrivilege);

        return devSysWithPrivilege;
      })
      .catch(error => {
        return user;
      });
  }

  const hasPrivilege = (p) => {
    if (!currentUser || currentUser.zone === "internet" || !currentUser.privileges)
      return false;
    return currentUser.privileges.includes(p);
  }

  const hasRole = (r) => {
    if (!currentUser || currentUser.zone === "internet")
      return false;
    return currentUser.role === r;
  }

  const value = {
    currentUser,
    logout,
    hasPrivilege,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {currentUser && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default ({ children }) => {
  const { msalConfig } = useConfig();
  const msalInstance = new PublicClientApplication(msalConfig);
  window.LiCompUiMsalInstance = msalInstance;

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        {children}
      </AuthProvider>
    </MsalProvider>
  )
}